import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import styles from "../../styles.module.scss";
import { MdDelete, MdEdit, MdShare } from 'react-icons/md';
import { TooltipTop } from '@components/tooltips';
import { HierarchyData } from '../types';
import { EyeIcon } from '@pages/workflow-analytics-page/assets/icons';
import ShareHierarchyModal from './shareHierarchyModal';
import { DataSourceHandler } from '@api/data-source-handler';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { useAppSelector } from '@store/hooks';

type HierachyListProps = {
  data: HierarchyData[] | null;
  handleDelete: (item: HierarchyData) => void;
  handleEdit: (item: HierarchyData) => void;
  handleViewCsvFileMetadata: (item: HierarchyData) => void;
};

const HierachyList: React.FC<HierachyListProps> = ({ data, handleDelete, handleEdit, handleViewCsvFileMetadata }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedHierarchyId, setSelectedHierarchyId] = useState<number | null>(null);
  const [accessSharedList, setAccessSharedList] = useState<any[]>([]);
  const [featureName, setFeatureName] = useState<string>("");
  const { envVariables: Env } = useSelector((store:RootState)=> store.AccountReducer);
  const currentUser = useAppSelector((state) => state.AccountReducer.activeUserInfo.email);
  const [shareMenuIndex, setShareMenuIndex] = useState<number | null>(null);
  const openShareModal = (id: number) => {
    setSelectedHierarchyId(id);
    setModalVisible(true);
  };

  const openUnShareModal = (id: number) => {
    DataSourceHandler.getAccessSharedList(
      `${Env?.REACT_APP_PLATFORM_URL}/asset/api`, 
      id,
      (response: any) => {
        if(response){
          setSelectedHierarchyId(id);
          setModalVisible(true);
          setAccessSharedList(response)
        }
      },
      (error: any) => {
        errorAlert(error);
      }
    );
  };

  const handleShareSubmit = (selectedUsers: string[]) => {
    const requestData = {
      hierarchyId: Number(selectedHierarchyId),
      userId: selectedUsers,
      action: featureName,
    };
    DataSourceHandler.ShareHierarchy(
      `${Env?.REACT_APP_PLATFORM_URL}/asset/api`, 
      requestData,
      (response: any) => {
        if(response){
          successAlert(response);
        }
      },
      (error: any) => {
        errorAlert(error);
      }
    );
  
    setModalVisible(false);
  };

  const useClickOutside=(callback: () => void) =>{
    const ref = useRef<HTMLDivElement | null>(null);
  
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          callback();
        }
      }
  
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [callback]);
  
    return ref;
  }

  const dropdownRef = useClickOutside(() => setShareMenuIndex(null));

  return (
    <div className={styles["assetHierarchyList"]}>
      <ul className={classnames(styles['nifi_titles'])}>
        {data?.map((item, index) => (
          <li key={index}>
            <div>
              <div className={styles['key']}>Hierarchy Name</div>
              <TooltipTop title={item.hierarchyName}>
              <div className={styles['hierarchyName']}>{item.hierarchyName}</div>
              </TooltipTop>
            </div>
            <div className={styles['divider']}></div>
            <div>
              <div className={styles['key']}>Edge Name</div>
              <div>{item.edgeName}</div>
            </div>
            <div className={styles['divider']}></div>


            <div className={styles.actions}>
              <TooltipTop title="Edit Hierarchy">
                <button
                  className={styles.actionButton}
                  onClick={() => handleEdit(item)}
                >
                  <div className={styles.center}>

                    <MdEdit size={"24px"} />
                  </div>
                </button>
              </TooltipTop>
              <TooltipTop title="View Hierarchy">
                <button
                  className={styles.actionButton}
                  onClick={() => handleViewCsvFileMetadata(item)}
                >
                  <div className={styles['center']}>
                    <EyeIcon />
                  </div>
                </button>
              </TooltipTop>
              <TooltipTop title="Share / Unshare">
                <div className={styles["shareWrapper"]}>
                  <button
                    className={styles["actionButton"]}
                    onClick={() => {
                      if (currentUser !== item.createdBy) {
                        errorAlert("Only admin of this hierarchy have the rights to share/unshare.");
                      } else {
                        setShareMenuIndex(index === shareMenuIndex ? null : index);
                      }
                    }}
                  >
                    <div className={styles["center"]}>
                      <MdShare size={"24px"} />
                    </div>
                  </button>

                  {shareMenuIndex === index && (
                    <div ref={dropdownRef} className={styles["shareDropdown"]}>
                      <button
                        onClick={() => {
                          openShareModal(item.id);
                          setFeatureName("share");
                          setShareMenuIndex(null);
                        }}
                      >
                        Share
                      </button>
                      <button
                        onClick={() => {
                          openUnShareModal(item.id);
                          setFeatureName("unshare");
                          setShareMenuIndex(null);
                        }}
                      >
                        Unshare
                      </button>
                    </div>
                  )}
                </div>
              </TooltipTop>
              <TooltipTop title="Delete Hierarchy">
                <button
                  className={styles.deleteButton}
                  onClick={() => {
                    if (currentUser !== item.createdBy) {
                      errorAlert("Only admin of this hierarchy have the rights to delete");
                    } else {
                      handleDelete(item);
                    }
                  }}
                >
                  <div className={styles.actionButton}>
                    <MdDelete size={"24px"} />
                  </div>
                </button>
              </TooltipTop>
            </div>
          </li>
        ))}
      </ul>

      <ShareHierarchyModal
        featureName={featureName}
        accessSharedList={featureName=='unshare'?accessSharedList:[]}
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSubmit={handleShareSubmit}
      />
    </div>
  );
};

export default HierachyList;
